import { type BriefContent, renderRichText } from '@sigora/domain';
import { useEffect, useId, useRef, useState } from 'react';

import { BRIEF_HEADINGS, MOVE_UP_LABEL, REORDERED_BY_YOU } from '../brief-copy.ts';
import { type RevisionMarks, UpdatedTag } from './RevisionMarks.tsx';
import { briefEditRefusal } from './brief-edit.ts';
import { NotSettledList } from './NotSettledList.tsx';
import type { BriefEdits } from './Verdict.tsx';

/**
 * The Recommendation, then what the study did not settle. `reordered` marks recommendations in the
 * researcher's order rather than Sigora's, and `marks` a block a Sigora revision changed. With
 * `edits`, every recommendation but the first can be moved up one place; what is not settled has no
 * controls anywhere.
 */
export const RecommendationList = ({
  recommendation,
  cards,
  reordered,
  edits,
  marks,
}: {
  recommendation: BriefContent['recommendation'];
  /** The Evidence cards, which name each research question a Not settled entry is about. */
  cards: BriefContent['evidence']['cards'];
  reordered: boolean;
  edits?: BriefEdits;
  marks?: RevisionMarks | null;
}) => {
  const idPrefix = useId();
  const [error, setError] = useState<string | null>(null);
  const moveButtons = useRef(new Map<string, HTMLButtonElement>());
  /**
   * The recommendation whose Move up was just answered, however it ended: its Move up takes the
   * focus back wherever the list now puts it. A new object each time, so a second answer for the
   * same recommendation moves the focus again.
   */
  const [answered, setAnswered] = useState<{ id: string } | null>(null);
  const ids = recommendation.recommendations.map((item) => item.id);
  const second = useRef<string | undefined>(undefined);
  second.current = ids[1];

  useEffect(() => {
    if (answered === null) {
      return;
    }
    // At the top it has no Move up, so the focus goes to the one below it.
    const target =
      moveButtons.current.get(answered.id) ?? moveButtons.current.get(second.current ?? '');
    target?.focus();
  }, [answered]);

  const moveUp = async (index: number) => {
    const id = ids[index];
    const above = ids[index - 1];
    if (edits === undefined || edits.busy || id === undefined || above === undefined) {
      return;
    }

    setError(null);
    const order = ids.map((each, at) => (at === index - 1 ? id : at === index ? above : each));
    const outcome = await edits.send({ op: 'reorder_recommendations', order });
    if (outcome.status === 'refused') {
      setError(briefEditRefusal(outcome.failure));
    }
    setAnswered({ id });
  };

  return (
    <>
      <h2>
        {BRIEF_HEADINGS.recommendation}
        {reordered && (
          <>
            {' '}
            <span className="brief-tag" data-test-id="brief-recommendation-reordered">
              {REORDERED_BY_YOU}
            </span>
          </>
        )}
        <UpdatedTag marks={marks} block="recommendation" />
      </h2>
      <ul className="brief-list">
        {recommendation.recommendations.map((item, index) => {
          const textId = `${idPrefix}-${item.id}`;

          return (
            <li key={item.id} data-test-id="brief-recommendation-item">
              <span id={textId} data-test-id="brief-recommendation-text">
                {renderRichText(item.text)}
              </span>
              {edits !== undefined && index > 0 && (
                <>
                  {' '}
                  <button
                    type="button"
                    className="text-link brief-inline-action"
                    data-test-id="brief-recommendation-up"
                    aria-describedby={textId}
                    disabled={edits.busy}
                    ref={(button) => {
                      if (button === null) {
                        moveButtons.current.delete(item.id);
                      } else {
                        moveButtons.current.set(item.id, button);
                      }
                    }}
                    onClick={() => void moveUp(index)}
                  >
                    {MOVE_UP_LABEL}
                  </button>
                </>
              )}
            </li>
          );
        })}
      </ul>
      {error !== null && (
        <p className="chat-error" data-test-id="brief-recommendation-error" role="alert">
          {error}
        </p>
      )}
      <NotSettledList entries={recommendation.not_settled} cards={cards} />
    </>
  );
};
